import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Slider } from '@/components/ui/slider';
import { toast } from 'sonner';
import { useGenerateSeedDrafts, useBulkCreateSeeds } from '@/hooks/use-seeds';
import type { SeedDraftItem } from '@/types/seeds';
import { AIDraftReviewTable } from './ai-draft-review-table';
import { Sparkles } from 'lucide-react';

export interface AIGenerateSeedsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  categories: string[];
  onSuccess: () => void;
}

export function AIGenerateSeedsDialog({
  open,
  onOpenChange,
  categories,
  onSuccess,
}: AIGenerateSeedsDialogProps) {
  const [category, setCategory] = useState<string>(categories[0] ?? '');
  const [newCategory, setNewCategory] = useState('');
  const [count, setCount] = useState(10);
  const [difficultyRange, setDifficultyRange] = useState<[number, number]>([1, 5]);
  const [toneAudience, setToneAudience] = useState('');
  const [drafts, setDrafts] = useState<SeedDraftItem[] | null>(null);
  const [selectedIndices, setSelectedIndices] = useState<Set<number>>(new Set());
  const [startingSeedNumber, setStartingSeedNumber] = useState(1);

  const generateMutation = useGenerateSeedDrafts();
  const createMutation = useBulkCreateSeeds();

  const resolvedCategory = category === '__new__' ? newCategory.trim() : category;

  const handleGenerate = async () => {
    if (!resolvedCategory) {
      toast.error('Pick a category');
      return;
    }
    if (count < 1 || count > 25) {
      toast.error('Count must be 1-25');
      return;
    }
    try {
      const res = await generateMutation.mutateAsync({
        category: resolvedCategory,
        count,
        difficulty_min: difficultyRange[0],
        difficulty_max: difficultyRange[1],
        tone_audience: toneAudience.trim() || undefined,
        language: 'en',
      });
      setDrafts(res.drafts);
      setSelectedIndices(new Set(res.drafts.map((_, i) => i)));
    } catch (e) {
      toast.error(`Generation failed: ${(e as Error).message}`);
    }
  };

  const handleSave = async () => {
    if (!drafts) return;
    const picked = drafts
      .map((d, i) => ({ d, i }))
      .filter(({ i }) => selectedIndices.has(i))
      .map(({ d }, idx) => ({
        problem_category: d.problem_category,
        seed_number: startingSeedNumber + idx,
        title: d.title,
        description: d.description,
        estimated_minutes: d.estimated_minutes,
        difficulty_level: d.difficulty_level,
      }));
    if (picked.length === 0) {
      toast.error('No drafts selected');
      return;
    }
    try {
      const result = await createMutation.mutateAsync({ seeds: picked });
      toast.success(`Created ${result.created} seed${result.created === 1 ? '' : 's'}`);
      reset();
      onOpenChange(false);
      onSuccess();
    } catch (e) {
      toast.error(`Save failed: ${(e as Error).message}`);
    }
  };

  const reset = () => {
    setDrafts(null);
    setSelectedIndices(new Set());
    setCategory(categories[0] ?? '');
    setNewCategory('');
    setCount(10);
    setDifficultyRange([1, 5]);
    setToneAudience('');
    setStartingSeedNumber(1);
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(v) => {
        if (!v) reset();
        onOpenChange(v);
      }}
    >
      <DialogContent className="sm:max-w-3xl">
        <DialogHeader>
          <DialogTitle>
            <Sparkles className="mr-2 inline h-4 w-4" />
            AI seed generation
          </DialogTitle>
          <DialogDescription>
            Generate a batch of seed drafts with AI, review them, and save the ones you want.
            Nothing is persisted until you click "Save selected".
          </DialogDescription>
        </DialogHeader>

        {!drafts ? (
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Category</Label>
              <Select value={category ?? ''} onValueChange={(v) => setCategory(v ?? '')}>
                <SelectTrigger>
                  <SelectValue placeholder="Pick a category" />
                </SelectTrigger>
                <SelectContent>
                  {categories.map((c) => (
                    <SelectItem key={c} value={c}>
                      {c}
                    </SelectItem>
                  ))}
                  <SelectItem value="__new__">+ New category…</SelectItem>
                </SelectContent>
              </Select>
              {category === '__new__' && (
                <Input
                  autoFocus
                  value={newCategory}
                  onChange={(e) => setNewCategory(e.target.value)}
                  placeholder="New category name"
                />
              )}
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Count</Label>
                <Input
                  type="number"
                  min={1}
                  max={25}
                  value={count}
                  onChange={(e) =>
                    setCount(Math.min(25, Math.max(1, Number(e.target.value) || 1)))
                  }
                />
              </div>
              <div className="space-y-2">
                <Label>Starting seed number</Label>
                <Input
                  type="number"
                  min={1}
                  value={startingSeedNumber}
                  onChange={(e) =>
                    setStartingSeedNumber(Math.max(1, Number(e.target.value) || 1))
                  }
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>
                Difficulty range: {difficultyRange[0]}–{difficultyRange[1]}
              </Label>
              <Slider
                min={1}
                max={5}
                step={1}
                value={difficultyRange}
                onValueChange={(v) => {
                  if (Array.isArray(v)) setDifficultyRange([v[0] ?? 1, v[1] ?? 5]);
                }}
              />
            </div>

            <div className="space-y-2">
              <Label>Tone / audience (optional)</Label>
              <Input
                value={toneAudience}
                onChange={(e) => setToneAudience(e.target.value)}
                placeholder="e.g. short, calming, for teenagers"
              />
            </div>
          </div>
        ) : (
          <AIDraftReviewTable
            drafts={drafts}
            selectedIndices={selectedIndices}
            onToggle={(i) => {
              setSelectedIndices((prev) => {
                const next = new Set(prev);
                if (next.has(i)) next.delete(i);
                else next.add(i);
                return next;
              });
            }}
            onUpdate={(i, patch) => {
              setDrafts((prev) => {
                if (!prev) return prev;
                const next = [...prev];
                next[i] = { ...next[i], ...patch };
                return next;
              });
            }}
          />
        )}

        <DialogFooter>
          {drafts ? (
            <>
              <Button variant="ghost" onClick={() => setDrafts(null)}>
                Back
              </Button>
              <Button onClick={handleSave} disabled={createMutation.isPending}>
                {createMutation.isPending
                  ? 'Saving…'
                  : `Save selected (${selectedIndices.size})`}
              </Button>
            </>
          ) : (
            <>
              <Button variant="ghost" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Button onClick={handleGenerate} disabled={generateMutation.isPending}>
                {generateMutation.isPending ? 'Generating…' : 'Generate drafts'}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
